import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { MantineProvider, Avatar, ActionIcon, Indicator, Notification } from '@mantine/core';
import { useUser } from '../src/context/UserContext';
import { Bell } from 'tabler-icons-react';
import { motion } from 'framer-motion';
import { Link } from '@inertiajs/react';
import ConditionalLink from '../components/ConditionalLink';

// Importing logos and images
import logoUrl from '/assets/NEW WCWD LOGO WHITE.svg';
import mobileLogoUrl from '/assets/Logomark_orange.svg';
import hamburgerIcon from '/assets/white-hamburger-menu.png';
import '../styles/Navbar.css';

interface NavbarProps {
  toggleSidebar: () => void;
  isLoginPage?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ toggleSidebar }) => {
  const { user, logout } = useUser(); // Use the context hook instead of local state
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationsCount, setNotificationsCount] = useState(0);

  const getLoginUrl = () => {
    const currentPath = window.location.pathname;
    return `/login?return_to=${encodeURIComponent(currentPath)}`;
  };

  const handleBellClick = () => {
    setShowNotification(true);

    setTimeout(() => {
      setShowNotification(false);
    }, 5000);
  };

  return (
    <MantineProvider>
      <div>
        <Sidebar
          isOpen={isSidebarOpen}
          closeSidebar={() => setSidebarOpen(false)}
          user={user} // Pass user to Sidebar
        />

        <nav className="navbar">
          <div className="navbar-content">
            {/* Desktop Logo */}
            <div className="desktop-logo">
              <a href="/" className="navbar-brand">
                <img src={logoUrl} alt="Logo" className="site-nav-logo" />
              </a>
            </div>

            {/* Mobile Logo */}
            <div className="mobile-logo">
              <a href="/" className="navbar-brand">
                <img src={mobileLogoUrl} alt="Logo" className="site-nav-logo" style={{ width: '45px' }} />
              </a>
            </div>

            {/* User Info */}
            <div className="flex items-center space-x-4 ml-auto mr-2">
              {user ? (
                <div className="navy-left flex items-center ml-auto">
                  <div className="notification-bell-container">
                    <Indicator
                      size={notificationsCount > 0 ? 16 : 8}
                      color="orange"
                      processing={notificationsCount > 0}
                      offset={3}
                    >
                      <motion.div whileTap={{ scale: 0.9 }} whileHover={{ rotate: [0, -5, 5, 0] }}>
                        <ActionIcon
                          size="lg"
                          variant="transparent"
                          onClick={handleBellClick}
                          className="notification-bell"
                        >
                          <Bell strokeWidth={1.75} color="gray" style={{ width: '30px', height: '30px' }} />
                        </ActionIcon>
                      </motion.div>
                    </Indicator>
                  </div>

                  {/* Notification Popup (Only Shows When Clicked) */}
                  {showNotification && (
                    <div className="absolute top-14 right-14 z-50">
                      <Notification
                        title="Coming Soon!"
                        color="orange"
                        withCloseButton={true}
                        onClose={() => setShowNotification(false)}
                      >
                        Notifications will be available in a future update!
                      </Notification>
                    </div>
                  )}
                  <div className="mobile-avatar">
                    <ConditionalLink href={`/users/${user.username}/user-settings`} className="" style={{}}>
                      <Avatar src={user.avatar_url || undefined} />
                    </ConditionalLink>
                  </div>
                  <div className="user-profile">
                    <ConditionalLink
                      href={`/users/${user.username}/user-settings`}
                      className="user-navbutton flex items-center"
                      style={{}}
                    >
                      {user.avatar_url ? (
                        <img src={user.avatar_url} className="img-circle avatar_small mr-2" alt="Avatar" />
                      ) : (
                        <img
                          src="https://dancewise.s3.amazonaws.com/WCWD+blank+profile2.png"
                          className="img-circle avatar_small"
                          alt="Default avatar"
                        />
                      )}
                      <div className="username-nav poppins">{user.username}</div>
                    </ConditionalLink>
                  </div>
                </div>
              ) : (
                <a href={getLoginUrl()} className="nav-link ms-2">
                  Log in
                </a>
              )}
            </div>

            {/* Sidebar Toggle */}
            <div className="hamburger-icon">
              <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="p-2 bg-transparent border-none">
                <img src={hamburgerIcon} alt="Hamburger Icon" className="w-6 h-6" />
              </button>
            </div>
          </div>
        </nav>
      </div>
    </MantineProvider>
  );
};

export default Navbar;
